/**
 * @file usePartFields.ts
 * @description Части BRAND, SEO, BLOG и CATALOG (Р2а) черновика с
 * автосохранением по полям (phase19.16 Р1; контракт §1 — частичное тело с
 * `expectedVersion`; §19 — вид каталога `options`).
 *
 * Реестр частей (OCP): чтение, сохранение и сборка тела — записью
 * `*_IO`. Состояние живёт в каркасе вида сайта (`useSiteDraftParts`):
 * панели берут его через `useSiteDraftPart("brand")` и правят поле через
 * `set("tagline", "…")`.
 */

import { CATALOG_BADGE_KEYS } from "@chaiberi/shared";
import { useCallback } from "react";

import { useAutosavePart, type AutosaveApi } from "./useAutosavePart";
import type { ApplyPart } from "./useThemeDraft";
import { siteView, type SiteCatalogPatch } from "../../lib/api-site-view";
import { blogPatch, brandPatch } from "../../lib/site-view/part-patches";
import type { SiteBlogDraft, SiteBrandDraft, SiteCatalogDraft, SitePartState, SiteViewDraft } from "../../lib/site-view/types";

export interface PartFieldsApi<V> extends AutosaveApi<V> {
  fields: V;
  set: <K extends keyof V & string>(key: K, value: V[K]) => void;
}

interface PartIo<V> {
  part: "BRAND" | "SEO" | "BLOG" | "CATALOG";
  field: "brand" | "seo" | "blog" | "catalog";
  save: (value: V, expectedVersion: number, keys: ReadonlySet<string>) => Promise<{ part: SitePartState; value: V }>;
  load: () => Promise<{ part: SitePartState; value: V }>;
}

const BRAND_IO: PartIo<SiteBrandDraft> = {
  part: "BRAND",
  field: "brand",
  save: async (value, expectedVersion, keys) => {
    const res = await siteView.saveBrand({ expectedVersion, ...brandPatch(value, keys) });
    return { part: res.part, value: res.brand };
  },
  load: async () => {
    const res = await siteView.brand();
    return { part: res.part, value: res.brand };
  },
};

const BLOG_IO: PartIo<SiteBlogDraft> = {
  part: "BLOG",
  field: "blog",
  save: async (value, expectedVersion, keys) => {
    const res = await siteView.saveBlog({ expectedVersion, ...blogPatch(value, keys) });
    return { part: res.part, value: res.blog };
  },
  load: async () => {
    const res = await siteView.blog();
    return { part: res.part, value: res.blog };
  },
};

/**
 * Тело правки вида каталога: параметры целиком; сервер сливает по полям
 * (§19.1), поэтому снятая своя подпись бейджа уходит явным `null`.
 */
export function catalogPatchOptions(options: SiteCatalogDraft["options"]): NonNullable<SiteCatalogPatch["options"]> {
  const value = options ?? {};
  const labels = value.badgeLabels ?? {};
  const badgeLabels = Object.fromEntries(CATALOG_BADGE_KEYS.map((key) => [key, labels[key] ?? null]));
  return { ...value, badgeLabels };
}

const CATALOG_IO: PartIo<SiteCatalogDraft> = {
  part: "CATALOG",
  field: "catalog",
  save: async (value, expectedVersion) => {
    const res = await siteView.saveCatalog({ expectedVersion, options: catalogPatchOptions(value.options) });
    return { part: res.part, value: res.catalog };
  },
  load: async () => {
    const res = await siteView.catalog();
    return { part: res.part, value: res.catalog };
  },
};

function usePartFieldsState<V extends object>(
  draft: SiteViewDraft | null,
  io: PartIo<V>,
  serverValue: V | null,
  applyPart: ApplyPart,
): PartFieldsApi<V> | null {
  const api = useAutosavePart<V>({
    serverValue,
    // Части CATALOG у сервера до Р2а нет — версия 0, правка покажет ошибку сохранения.
    serverVersion: draft?.parts[io.part]?.version ?? 0,
    save: io.save,
    load: io.load,
    onSaved: (result) => applyPart(result.part, { [io.field]: result.value }),
  });
  const fields = api.value ?? serverValue;
  const { change } = api;
  const set = useCallback(
    <K extends keyof V & string>(key: K, value: V[K]) => {
      if (fields) change({ ...fields, [key]: value }, [key]);
    },
    [change, fields],
  );
  return fields ? { ...api, fields, set } : null;
}

export function useBrandDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): PartFieldsApi<SiteBrandDraft> | null {
  return usePartFieldsState(draft, BRAND_IO, draft?.brand ?? null, applyPart);
}

/** Пустой вид каталога — у черновика до Р2а поля нет (читается умолчаниями shared). */
const EMPTY_CATALOG: SiteCatalogDraft = { options: {} };

export function useCatalogDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): PartFieldsApi<SiteCatalogDraft> | null {
  return usePartFieldsState(draft, CATALOG_IO, draft ? (draft.catalog ?? EMPTY_CATALOG) : null, applyPart);
}

export function useBlogDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): PartFieldsApi<SiteBlogDraft> | null {
  return usePartFieldsState(draft, BLOG_IO, draft?.blog ?? null, applyPart);
}
